// Customer accounts (Ordre 07): the business words of the three axes and the small pure rules the screens share. UI only — the
// server checks every right and every transition again.
import { hasPermission } from "./permissions";

export const CUSTOMER_READ_KEYS = ["customers:view", "customers:view_all"];
export const CUSTOMER_WRITE_KEYS = ["customers:manage", "customers:manage_all"];

// The pipeline stages of a converted pharmacy: the « Client » tab of the fiche is offered on them.
export const CONVERTED_STAGES = ["won", "onboarding_in_progress", "active_client"];

export const PHASE_LABELS = {
  to_start: "À démarrer",
  onboarding: "Onboarding en cours",
  onboarding_done: "À déclarer en service",
  live: "En service",
};

export const STATUS_LABELS = {
  pre_activation: "Avant mise en service",
  active: "Actif",
  suspended: "Suspendu",
  terminated: "Clos",
};

export const CLOSE_LABELS = {
  terminate: "Résilié",
  renounce: "Renoncement avant mise en service",
  commercial_reverted: "Retour en arrière commercial",
};

// Health (07-3, D-15): one state and the facts behind it. « Inconnue » = nothing proves the follow-up is going well.
export const HEALTH_LABELS = {
  on_track: "Bon suivi",
  attention: "À surveiller",
  at_risk: "À risque",
  unknown: "Inconnue",
};
export const HEALTH_CHOICES = ["at_risk", "attention", "unknown", "on_track"];
export const OVERRIDE_CHOICES = ["on_track", "attention", "at_risk"];

const HEALTH_TONES = {
  on_track: "bg-emerald-50 text-emerald-800 border-emerald-200",
  attention: "bg-amber-50 text-amber-800 border-amber-200",
  at_risk: "bg-red-50 text-red-800 border-red-200",
  unknown: "bg-slate-100 text-slate-600 border-slate-200",
};

export function healthTone(state) {
  return HEALTH_TONES[state] || HEALTH_TONES.unknown;
}

export const OBJECTIVE_STATE_LABELS = { in_progress: "En cours", reached: "Atteint", dropped: "Abandonné" };

// `short`: the phone's label, so the three groups always fit side by side on a narrow screen.
export const GROUPS = [
  { id: "deploying", label: "À démarrer / en mise en service", short: "En démarrage" },
  { id: "live", label: "En service", short: "En service" },
  { id: "closed", label: "Clos", short: "Clos" },
];

const PHASE_TONES = {
  to_start: "bg-slate-100 text-slate-700 border-slate-200",
  onboarding: "bg-sky-50 text-sky-800 border-sky-200",
  onboarding_done: "bg-amber-50 text-amber-800 border-amber-200",
  live: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const STATUS_TONES = {
  pre_activation: "bg-slate-100 text-slate-700 border-slate-200",
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  suspended: "bg-amber-50 text-amber-800 border-amber-200",
  terminated: "bg-slate-200 text-slate-700 border-slate-300",
};

export function phaseTone(phase) {
  return PHASE_TONES[phase] || PHASE_TONES.to_start;
}

export function statusTone(status) {
  return STATUS_TONES[status] || STATUS_TONES.pre_activation;
}

export function canSeeCustomers(user) {
  return !!user?.features?.customers && hasPermission(user, CUSTOMER_READ_KEYS);
}

export function canManageCustomers(user) {
  return !!user?.features?.customers && hasPermission(user, CUSTOMER_WRITE_KEYS);
}

// The one short label of a pharmacy's account next to its name (pipeline card, prospects list, search): the status when the
// account is closed or suspended, otherwise its phase.
export function customerBadge(badge) {
  if (!badge) return null;
  if (badge.status === "terminated") return { label: "Client · Clos", tone: statusTone("terminated") };
  if (badge.status === "suspended") return { label: "Client · Suspendu", tone: statusTone("suspended") };
  return { label: `Client · ${PHASE_LABELS[badge.phase] || "Dossier"}`, tone: phaseTone(badge.phase) };
}

export function groupOf(account) {
  if (account.status === "terminated") return "closed";
  return account.phase === "live" ? "live" : "deploying";
}

// The main action of the « Client » tab, from the server's capabilities: one primary button, the others in the menu.
export function primaryAction(capabilities = {}) {
  for (const id of ["activate", "resume", "reopen"]) {
    if (capabilities[id]) return id;
  }
  return null;
}

export const STATUS_ACTIONS = {
  suspend: { to: "suspended", title: "Suspendre le dossier", button: "Suspendre", date: false, danger: false,
    help: "Le client reste en service dans Jamora : la suspension ne coupe rien, elle signale que le suivi est en pause." },
  resume: { to: "active", title: "Reprendre le dossier", button: "Reprendre", date: false, danger: false,
    help: "Le dossier redevient actif." },
  terminate: { to: "terminated", title: "Résilier le dossier", button: "Résilier", date: true, danger: true,
    help: "Le résultat commercial reste « Gagné » dans le pipeline ; seul le dossier client se ferme. Rien ne change dans Jamora." },
  renounce: { to: "terminated", title: "Renoncer avant la mise en service", button: "Renoncer", date: true, danger: true,
    help: "Le résultat commercial reste inchangé dans le pipeline ; le dossier client se ferme avant toute mise en service." },
  reopen: { to: null, title: "Rouvrir le dossier", button: "Rouvrir", date: false, danger: false,
    help: "Le dossier reprend son statut d'avant la fermeture (en service s'il l'était)." },
};

// The status a reopened account goes back to (the server refuses any other).
export function reopenTarget(account) {
  return account?.phase === "live" ? "active" : "pre_activation";
}

export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export function formatDay(value) {
  if (!value) return null;
  const day = String(value).slice(0, 10);
  const [y, m, d] = day.split("-");
  if (!y || !m || !d) return null;
  return `${d}/${m}/${y}`;
}

const HISTORY_ACTIONS = {
  "phase:live": "Mise en service déclarée",
  "status:suspended": "Dossier suspendu",
  "status:terminated": "Dossier clos",
  "status:active": "Dossier actif",
  "status:pre_activation": "Dossier rouvert",
};
const FIELD_LABELS = {
  account_manager_user_id: "Responsable du compte",
  contract_signed_at: "Date de signature",
  next_review_date: "Prochaine revue",
  service_activated_at: "Date de mise en service",
};

// One line of the account's history, in business words.
export function historyLabel(entry) {
  if (entry.axis === "phase" && entry.from == null) return "Dossier ouvert";
  if (entry.axis === "phase" && entry.to !== "live") return `Phase : ${PHASE_LABELS[entry.to] || entry.to}`;
  if (entry.axis === "tenant") return entry.to === "closed" ? "Liaison Jamora close" : entry.to === "verified" ? "Liaison Jamora vérifiée" : "Liaison Jamora déclarée";
  if (entry.axis === "manager") return entry.to ? "Responsable du compte changé" : "Responsable du compte retiré";
  if (entry.axis === "health_override") return entry.to ? `Santé corrigée : ${HEALTH_LABELS[entry.to] || entry.to}` : "Correction de santé levée";
  if (entry.axis === "report") return entry.field ? `Compte rendu de mission publié (${entry.field})` : "Compte rendu de mission publié";
  if (entry.axis === "dates") return `${FIELD_LABELS[entry.field] || "Date"} : ${formatDay(entry.to) || "effacée"}`;
  return HISTORY_ACTIONS[`${entry.axis}:${entry.to}`] || "Dossier mis à jour";
}

// Quick views of the portfolio (D-40): each one is a filter of the address, so a link or the dashboard lands on it.
export const QUICK_VIEWS = [
  { id: "at_risk", label: "À risque", params: { health: "at_risk" } },
  { id: "review", label: "Revue dépassée", params: { review: "overdue" } },
];

export function customerErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.msg) return detail.msg;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) return "Vérifiez les champs saisis.";
  return fallback;
}
